/**
 * Count menu: clickable list of count types to go inside mp-drop-menu
 */
import {AttrsSchema} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChildMenu, ChildMenuBaseAttrs} from 'common/query-builder/query-block/child-menu';
import {AdvancedMenuValue, CountBy, CountType} from 'common/query-builder/query-entry-section/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {MATH_TYPE, ResourceType} from 'common/report/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {assert} from 'common/util';
import {getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {ChangeEvent} from 'common/components/select/types';

import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {BehaviorType} from '../constants';
import {IncludeCustomPropertyType, isProperty} from '../properties-menu/types';
import {
  getPropertyDefinitions,
  getTooltipNameForPropertyItem,
  getTooltipPathForPropertyItem,
} from '../properties-menu/util';
import {
  AggregationSelector,
  CountTypeSelector,
  FUNNELS_COUNT_MENU_ITEMS,
  INSIGHTS_COUNT_MENU_ITEMS,
  RETENTION_COUNT_MENU_ITEMS,
  SubSelectionTypes,
} from './constants';
import {
  generateSectionItem,
  getPropertyName,
  getSelectedCountByLabel,
  getSubSelectionsForCountType,
  shouldCommitSelection,
} from './util';
import css from './index.inline.styl';
import {template} from './template';

interface CountMenuState {
  isSelectorHovered: boolean; // is selector hovered
  isPropertyMenuOpen: boolean;
  isAggregationPerUserMenuOpen: boolean;
  isAggregationMenuOpen: boolean;
  isAdvancedMenuOpen: boolean;
  // selections
  selectedCountType: CountType;
  selectedCountTypeIndex: number;
  selectedProperty: PropertySectionItem;
  selectedAggregationOperator: MATH_TYPE;
  selectedAggregationOperatorPerUser: MATH_TYPE;
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
}

interface CountMenuAttrs {
  'data-groups'?: DataGroup[];
  'enable-workspace-filter-restrictions'?: boolean;
  'include-custom-properties'?: IncludeCustomPropertyType;
  'advanced-menu-sections'?: Array<Section<AdvancedMenuValue>>;
}

type CountMenuJsxProps = {
  attrs?: ChildMenuBaseAttrs<CountBy> & CountMenuAttrs;
  on?: {
    select?: (ev: SelectCountByEvent) => void;
  };
};

type SelectCountByEvent = CustomEvent<CountBy>;

export class CountMenu extends ChildMenu<CountMenuState, CountMenuAttrs, CountBy> {
  static get attrsSchema(): AttrsSchema<ChildMenuBaseAttrs<CountBy> & CountMenuAttrs> {
    return {
      ...super.attrsSchema,
      'data-groups': {
        type: `json`,
        default: [],
      },
      'advanced-menu-sections': {
        type: `json`,
        default: [],
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        isSelectorHovered: false,
        isPropertyMenuOpen: false,
        isAggregationPerUserMenuOpen: false,
        isAggregationMenuOpen: false,
        isAdvancedMenuOpen: false,
        selectedCountType: null,
        selectedCountTypeIndex: -1,
        selectedProperty: null,
        selectedAggregationOperator: null,
        selectedAggregationOperatorPerUser: null,
        countTypeSelections: [],
      },
      helpers: {
        ...super.config.helpers,
      },
    };
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `data-group-id`) {
      this.update({
        countTypeSelections: this.getCountTypeSelections(),
      });
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    const countTypeSelections = this.getCountTypeSelections();
    const {countType, aggregationOperator, aggregationOperatorPerUser, property} = this.attr(`selected`);
    const selectedCountTypeIndex = countTypeSelections.findIndex((option) => option.value.countType === countType);
    this.update({
      countTypeSelections,
      selectedCountType: countType,
      selectedCountTypeIndex,
      selectedAggregationOperator: aggregationOperator,
      selectedAggregationOperatorPerUser: aggregationOperatorPerUser,
      selectedProperty: property,
    });
  }

  /** Defines how the output copy will look like */
  public selectedLabel() {
    return getSelectedCountByLabel({
      behaviorType: this.attr(`behavior-type`),
      selected: this.attr(`selected`),
      advancedMenuSections: this.attr(`advanced-menu-sections`),
      dataGroupId: this.attr(`data-group-id`),
      dataGroups: this.attr(`data-groups`),
    });
  }

  public selectedBrowserTooltipLabel() {
    return `Measuring ${this.selectedLabel()}`;
  }

  /** List of selections that will be highlighted in the output copy */
  public getSelections(): string[] {
    return [getPropertyName(this.state.selectedProperty)];
  }

  public selectedRightIcon() {
    // if selector is hovered or the count menu is open, display icon in selector
    if (this.state.isSelectorHovered || this.attr(`open`)) {
      return `caret-down`;
    }
    return ``;
  }

  public handleLabelMouseEnter() {
    this.update({
      isSelectorHovered: true,
    });
  }

  public handleLabelMouseLeave() {
    this.update({
      isSelectorHovered: false,
    });
  }

  public handleCloseMenu() {
    // If the current count type does not have required selections, set the count type selection state back to previous selections
    const {
      countTypeSelections,
      selectedCountTypeIndex,
      selectedProperty,
      selectedAggregationOperatorPerUser,
      selectedAggregationOperator,
    } = this.state;
    const currentCountTypeSelection = countTypeSelections[selectedCountTypeIndex];
    const commit = shouldCommitSelection({
      countTypeSelection: currentCountTypeSelection,
      property: selectedProperty,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      aggregationOperator: selectedAggregationOperator,
    });
    if (!commit) {
      const {countType, aggregationOperator, aggregationOperatorPerUser, property} = this.attr(`selected`);
      this.update({
        selectedCountType: countType,
        selectedCountTypeIndex: countTypeSelections.findIndex((option) => option.value.countType === countType),
        selectedAggregationOperator: aggregationOperator,
        selectedAggregationOperatorPerUser: aggregationOperatorPerUser,
        selectedProperty: property,
      });
    }
  }

  public handleAdvancedMenuChange = (ev: ChangeEvent<AdvancedMenuValue>) => {
    const advancedMenuValue = ev.detail.item.value;
    const {
      selectedCountType,
      selectedProperty,
      selectedAggregationOperator,
      selectedAggregationOperatorPerUser,
    } = this.state;
    const countBy: CountBy = {
      countType: selectedCountType,
      aggregationOperator: selectedAggregationOperator,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      property: selectedProperty,
      advancedMenuValue,
    };
    this.commitCountBy(countBy);
  };

  public getCountTypeSelections(): Array<SectionItem<CountTypeSelector>> {
    const behaviorType = this.attr(`behavior-type`);
    switch (behaviorType) {
      case BehaviorType.Simple:
        return INSIGHTS_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      case BehaviorType.Funnel:
        return FUNNELS_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      case BehaviorType.Retention:
        return RETENTION_COUNT_MENU_ITEMS.map((item) =>
          generateSectionItem({
            item,
            dataGroupId: this.attr(`data-group-id`),
            dataGroups: this.attr(`data-groups`),
          }),
        );
      default:
        return [];
    }
  }

  /**
   * Specifies if the current sub selection can be expanded or not
   * If the previous sub selection has selected value, current sub selection can be expanded
   */
  public canDisplaySubSelection(countTypeIndex: number, previousSelectionType: SubSelectionTypes) {
    if (this.state.selectedCountTypeIndex !== countTypeIndex) {
      return false;
    }
    switch (previousSelectionType) {
      case SubSelectionTypes.Property:
        return !!this.state.selectedProperty;
      case SubSelectionTypes.AggregationPerUser:
        return !!this.state.selectedAggregationOperatorPerUser;
      case SubSelectionTypes.Aggregation:
        return !!this.state.selectedAggregationOperator;
      default:
        return !!this.state.selectedCountType;
    }
  }

  /** Open sub selection menu */
  public openSubSelectionMenu(subSelections, subSelectionIndex) {
    const subSelection = subSelections[subSelectionIndex];
    switch (subSelection) {
      case SubSelectionTypes.Property:
        this.update({
          isPropertyMenuOpen: true,
        });
        break;
      case SubSelectionTypes.AggregationPerUser:
        this.update({
          isAggregationPerUserMenuOpen: true,
        });
        break;
      case SubSelectionTypes.Aggregation:
        this.update({
          isAggregationMenuOpen: true,
        });
        break;
    }
  }

  public closeAllSubMenus() {
    this.update({
      isPropertyMenuOpen: false,
      isAggregationPerUserMenuOpen: false,
      isAggregationMenuOpen: false,
      isAdvancedMenuOpen: false,
    });
  }

  public disableTooltips() {
    const {isPropertyMenuOpen, isAggregationPerUserMenuOpen, isAggregationMenuOpen} = this.state;
    // disable first level selection (Count Type) tooltips if any of the sub menus are open
    return isPropertyMenuOpen || isAggregationPerUserMenuOpen || isAggregationMenuOpen;
  }

  public getPropertyTooltipData(selectedProperty: PropertySectionItem): ContextPanelData {
    assert(isProperty(selectedProperty));
    const dataGroupId = this.attr(`data-group-id`);
    const dataGroups = this.attr(`data-groups`);
    const canAccessUserProperties = this.attr(`project-has-people`);
    const shouldShowEventProps = !this.attr(`hide-events`);
    const tooltipArgs = {
      name: getTooltipNameForPropertyItem(selectedProperty),
      path: getTooltipPathForPropertyItem(selectedProperty, dataGroups[dataGroupId]),
      resourceType: selectedProperty.resourceType,
      dataType: selectedProperty.type,
      dataGroupId,
      dataGroupName: dataGroups[dataGroupId]?.displayName,
      customPropertyId: selectedProperty.customPropertyId as string,
      isLookupTableProperty: selectedProperty.isLookupTableProperty,
    };
    const resourceTypes = [
      canAccessUserProperties ? ResourceType.User : null,
      shouldShowEventProps ? ResourceType.Event : null,
    ].filter(Boolean);
    const propertyDefinitions = getPropertyDefinitions(resourceTypes);
    const definitions = propertyDefinitions[selectedProperty.resourceType];
    return getTooltipDataForProperty(tooltipArgs, definitions, {
      allowEdit: false,
      allowDescriptionEdit: false,
      lookupTables: {},
      iconReferenceType: `dataType`,
    });
  }

  /**
   * Handles first level selections (Count Types) - i.e. Uniques, Total Events, Sessions
   * @param selection selected count type
   * @param index index of count type within this.state.countTypeSelections
   */
  public selectCountType(selection: SectionItem<CountTypeSelector>, index: number) {
    // De-select existing selections if different options
    if (this.state.selectedCountTypeIndex !== index) {
      this.update({
        selectedAggregationOperatorPerUser: null,
        selectedAggregationOperator: null,
        selectedProperty: null,
      });
    }

    // Open sub selection menu if exist
    const subSelections = getSubSelectionsForCountType(selection);
    this.openSubSelectionMenu(subSelections, 0);

    const {countType, operator} = selection.value;
    this.update({
      selectedCountType: countType,
      selectedCountTypeIndex: index,
      ...(operator && {
        selectedAggregationOperator: operator,
      }),
    });

    const commit = shouldCommitSelection({
      countTypeSelection: selection,
      ...(operator && {
        aggregationOperator: operator,
      }),
    });
    if (commit) {
      const countBy: CountBy = {
        countType,
        aggregationOperator: operator,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selection (Property) in Sub-selections
   * @param ev Select event from PropertiesMenu
   * @param subSelectionIndex index of SubSelections
   */
  public selectProperty(ev, subSelectionIndex) {
    const {item: property} = ev.detail;
    const {
      countTypeSelections,
      selectedCountType,
      selectedCountTypeIndex,
      selectedAggregationOperator,
      selectedAggregationOperatorPerUser,
    } = this.state;
    const countTypeSelection = countTypeSelections[selectedCountTypeIndex];

    this.update({
      selectedProperty: property,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType(countTypeSelection);
    this.openSubSelectionMenu(subSelections, subSelectionIndex + 1);

    const commit = shouldCommitSelection({
      countTypeSelection,
      property,
      aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
      aggregationOperator: selectedAggregationOperator,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: selectedCountType,
        aggregationOperator: selectedAggregationOperator,
        aggregationOperatorPerUser: selectedAggregationOperatorPerUser,
        property,
      };
      this.commitCountBy(countBy);
    }
  }

  /**
   * Handles second level selections (AggregationPerUser, Aggregation) in Sub-selections
   * @param aggregation selected aggregation section item from Aggregation Selection
   * @param subSelectionIndex index of SubSelections
   */
  public selectAggregation(aggregation: SectionItem<AggregationSelector>, subSelectionIndex: number) {
    const {operator, perUserOperator} = aggregation.value;
    const {countTypeSelections, selectedCountType, selectedCountTypeIndex, selectedProperty} = this.state;
    const countTypeSelection = countTypeSelections[selectedCountTypeIndex];

    this.update({
      selectedAggregationOperatorPerUser: perUserOperator,
      selectedAggregationOperator: operator,
    });

    // Open next sub selection menu if exist
    const subSelections = getSubSelectionsForCountType(countTypeSelection);
    this.openSubSelectionMenu(subSelections, subSelectionIndex + 1);

    const commit = shouldCommitSelection({
      countTypeSelection,
      property: selectedProperty,
      aggregationOperatorPerUser: perUserOperator,
      aggregationOperator: operator,
    });
    if (commit) {
      const countBy: CountBy = {
        countType: selectedCountType,
        aggregationOperator: operator,
        aggregationOperatorPerUser: perUserOperator,
        property: selectedProperty,
      };
      this.commitCountBy(countBy);
    }
  }

  public commitCountBy(countBy: CountBy) {
    this.closeAllSubMenus();
    const selectEvent = new CustomEvent(`select`, {detail: {item: countBy}});
    this.dispatchEvent(selectEvent);
  }
}

export const CountMenuEl = defineAndMakeJsxFactory<CountMenuJsxProps>(`mp-count-menu`, CountMenu);
export const CountMenuReact = panel2react<CountMenuJsxProps>(`mp-count-menu`, CountMenu);
